import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  Chip,
  useTheme,
  IconButton,
  CircularProgress,
} from '@material-ui/core'
import { Close as CloseIcon, Psychology as AIIcon, Check as CheckIcon } from '@material-ui/icons'
import ClearIcon from '@material-ui/icons/Clear'
import { Alert } from '@material-ui/lab'
import HPOFilterWrapper from 'src/components/HPOFilterWrapper'
import { getHPOFromClinicalHistory } from 'src/api/HPO_suggestion/hpo_suggestion'
import { runAIOnAnalysis } from 'src/api/analysis/runAI'
import { alpha } from '@material-ui/core/styles'

const RunAIModal = ({ open, onClose, analysisData, onSuccess }) => {
  const theme = useTheme()

  const [clinicalHistory, setClinicalHistory] = useState('')
  const [hpos, setHpos] = useState({ hpos: [], hpoNames: [], hpoTerms: [] })
  const [userGuidance, setUserGuidance] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // HPO suggestion state
  const [loadingHPO, setLoadingHPO] = useState(false)
  const [hpoSuggestions, setHpoSuggestions] = useState([])
  const [originalSuggestions, setOriginalSuggestions] = useState([])
  const [rejectedHpoIds, setRejectedHpoIds] = useState([])
  const debounceTimerRef = useRef(null)
  const abortControllerRef = useRef(null)

  const isValid = clinicalHistory.trim().length > 0 && hpos.hpos.length > 0

  // Debounced HPO suggestion fetch
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (clinicalHistory && clinicalHistory.trim() !== '' && open) {
      debounceTimerRef.current = setTimeout(() => {
        fetchHPOs(clinicalHistory)
      }, 800)
    } else if (!clinicalHistory || clinicalHistory.trim() === '') {
      setHpoSuggestions([])
      setOriginalSuggestions([])
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalHistory, open])

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchHPOs = async (clinicalHistoryText) => {
    if (!clinicalHistoryText || clinicalHistoryText.trim() === '') return

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setLoadingHPO(true)
    try {
      const response = await getHPOFromClinicalHistory(clinicalHistoryText)

      if (abortControllerRef.current?.signal.aborted) return

      if (response.success) {
        const selectedHpoIds = hpos?.hpoTerms?.map((term) => term.hpo_id) || []

        const newSuggestions = response.hpo_names
          .filter((hpoText) => {
            const hpId = hpoText.match(/HP:\d+/)?.[0]
            return hpId && !selectedHpoIds.includes(hpId) && !rejectedHpoIds.includes(hpId)
          })
          .slice(0, 99)

        setHpoSuggestions(newSuggestions)
        setOriginalSuggestions(response.hpo_names)
      }
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('[HPO Suggestion Error]', error)
    } finally {
      setLoadingHPO(false)
    }
  }

  const handleAcceptHPO = (hpoText) => {
    const hpId = hpoText.match(/HP:\d+/)?.[0]
    const nameMatch = hpoText.match(/Name: ([^\n]+)/)
    const name = nameMatch ? nameMatch[1] : hpId

    if (hpId) {
      const newHpoTerm = {
        hpo_id: hpId,
        name: name,
        value: hpId,
        label: `${name} (${hpId})`,
        synonyms: [],
      }

      const currentHpoTerms = hpos?.hpoTerms || []
      const existingIds = currentHpoTerms.map((term) => term.hpo_id)

      if (!existingIds.includes(hpId)) {
        const newHpoTerms = [...currentHpoTerms, newHpoTerm]
        setHpos({
          hpos: newHpoTerms.map((term) => term.hpo_id),
          hpoNames: newHpoTerms.map((term) => term.name),
          hpoTerms: newHpoTerms,
        })
      }
    }

    setHpoSuggestions((prev) => prev.filter((h) => h !== hpoText))
  }

  const handleRejectHPO = (hpoText) => {
    const hpId = hpoText.match(/HP:\d+/)?.[0]
    if (hpId) {
      setRejectedHpoIds((prev) => [...prev, hpId])
    }
    setHpoSuggestions((prev) => prev.filter((h) => h !== hpoText))
  }

  const handleHpoChange = (newHpoData) => {
    setHpos(newHpoData)
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      onClose()
    }
  }

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await runAIOnAnalysis(analysisData.analysis_id, {
        clinical_history: clinicalHistory,
        hpos: hpos.hpos,
        hpo_names: hpos.hpoNames,
        diseases: [],
        disease_ids: [],
        user_guidance: userGuidance,
      })

      if (result?.error) {
        if (result.needed_balance) {
          setError(`Insufficient balance. You need ${result.needed_balance} more credits.`)
        } else {
          setError(result.error)
        }
        return
      }

      onSuccess?.()
      handleClose()
    } catch (err) {
      setError('Failed to start AI analysis. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: theme.palette.secondary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Run AI Analysis
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
          Provide clinical context to help AI prioritize relevant variants for{' '}
          <strong>{analysisData?.sample_name}</strong>.
        </Typography>

        <Grid container spacing={3}>
          {/* Clinical History */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Clinical History & Context
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: `${alpha(theme.palette.warning.main, 0.08)}`,
                    borderRadius: '12px',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.19)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Required
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1.5 }}>
                Provide detailed clinical information to help AI prioritize relevant variants. HPO suggestions will
                appear as you type.
              </Typography>

              {/* HPO suggestion loading indicator */}
              {loadingHPO && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Analyzing clinical history...
                  </Typography>
                </Box>
              )}

              {/* HPO suggestion chips */}
              {hpoSuggestions && hpoSuggestions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                    Suggested HPO Terms:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {hpoSuggestions.map((hpo, index) => {
                      const hpId = hpo.match(/HP:\d+/)?.[0] || hpo
                      const nameMatch = hpo.match(/Name: ([^\n]+)/)
                      const name = nameMatch ? nameMatch[1] : hpId
                      const chipLabel = name && name !== hpId ? `${name} (${hpId})` : hpId

                      return (
                        <Chip
                          key={index}
                          label={chipLabel}
                          deleteIcon={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAcceptHPO(hpo)
                                }}
                                sx={{
                                  padding: '2px',
                                  color: theme.palette.success.main,
                                  '&:hover': { backgroundColor: `${alpha(theme.palette.success.main, 0.13)}` },
                                }}
                              >
                                <CheckIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRejectHPO(hpo)
                                }}
                                sx={{
                                  padding: '2px',
                                  color: theme.palette.error.main,
                                  '&:hover': { backgroundColor: `${alpha(theme.palette.error.main, 0.13)}` },
                                }}
                              >
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </Box>
                          }
                          onDelete={() => {}}
                          sx={{
                            maxWidth: '300px',
                            '& .MuiChip-label': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                            '&:hover': {
                              backgroundColor: 'primary.light',
                            },
                            transition: 'background-color 0.2s',
                          }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                value={clinicalHistory}
                onChange={(e) => setClinicalHistory(e.target.value)}
                placeholder="Describe patient history, symptoms, family history, and any other relevant clinical details..."
                variant="outlined"
                multiline
                rows={4}
                size="small"
              />
            </Box>
          </Grid>

          {/* HPO Terms */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 2,
                border:
                  hpos.hpos.length === 0
                    ? `1px solid ${alpha(theme.palette.warning.main, 0.25)}`
                    : `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Phenotype Terms (HPO)
                </Typography>
                {hpos.hpos.length === 0 && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: `${alpha(theme.palette.warning.main, 0.08)}`,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.19)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.warning.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                      }}
                    >
                      Required
                    </Typography>
                  </Box>
                )}
              </Box>
              <HPOFilterWrapper value={hpos.hpoTerms || []} onChange={handleHpoChange} arrayOutput={true} label="" />
            </Box>
          </Grid>

          {/* Analysis Guidance */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Analysis Guidance
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1.5 }}>
                Specific instructions for the AI analysis (optional)
              </Typography>
              <TextField
                fullWidth
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder="Provide specific analysis instructions, focus areas, or any particular genes/pathways of interest..."
                variant="outlined"
                multiline
                rows={3}
                size="small"
              />
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isValid || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
        >
          {isSubmitting ? 'Starting...' : 'Run AI'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RunAIModal
